import { WorldManager as SharedWorldManager } from 'entangled-worlds-orbiters-shared/world';

/**
 * Orbiters-specific extension point for the shared WorldManager. This will host
 * custom geometry and audio-reactive variants that are unique to Orbiters.
 */
export class WorldManagerExtended extends SharedWorldManager {
  constructor(scene, options = {}) {
    super(scene, options);

    const { customCreators = {}, getRenderer = null } = options;

    // The renderer is READ LIVE, never kept.
    //
    // `WorldSceneController` creates and destroys its renderer over the voice's life: the no-3D mode
    // releases the context outright, and a voice in a realm borrows the compositor's, which the realm
    // swaps out from under it. The base manager stores whatever renderer it was handed at
    // construction, so a manager built while there was none — a realm voice booting with no
    // compositor — held `null` for good, and one built before a toggle held a disposed context.
    //
    // Either way the world builders were then handed a renderer that does not draw this voice, and
    // the texture loaders REQUIRE the real one (`getKTX2LoaderForRenderer` throws without it). That
    // throw travelled all the way out of session bootstrap and killed the card. Reading through to
    // the controller means the answer is whatever is true at the moment the world is built.
    if (typeof getRenderer === 'function') {
      Object.defineProperty(this, 'renderer', {
        configurable: true,
        enumerable: true,
        get: () => getRenderer() ?? null,
        // The controller owns this renderer's lifecycle, so an assignment here cannot be honoured —
        // but it must not vanish either. A silently swallowed write is how a caller comes to believe
        // it has re-bound the manager when it has not, which is the class of bug this getter exists
        // to end. Say so instead. (A setter has to exist at all because the base constructor's own
        // assignment runs above, before this property replaces it.)
        set: () => {
          console.warn(
            '[WorldManagerExtended] renderer is read live from the scene controller — assignment ignored.',
          );
        },
      });
    }
    Object.entries(customCreators).forEach(([mode, creator]) => {
      const numericMode = Number(mode);
      if (!Number.isNaN(numericMode) && typeof creator === 'function') {
        this.registerWorldCreator(numericMode, creator);
      }
    });
  }

  /**
   * Allows registering additional world modes after construction.
   * @param {number} mode
   * @param {(args: object) => Promise<THREE.Object3D>} creator
   */
  registerCustomMode(mode, creator) {
    if (typeof creator !== 'function') {
      throw new Error('[WorldManagerExtended] creator must be a function');
    }
    this.registerWorldCreator(mode, creator);
  }
}
